/**
 * Track BCW — booking by condition and waitlists: the shapes RxManager_BE answers with, for the
 * booking app and the desktop. See RxManager_BE `docs/09-track-bcw-build-status.md`.
 */
import type { PublicBookingFormAnswers, PublicBookingResult } from './scheduling';

// ── Booking modes (BCW-D6) ───────────────────────────────────────────────────

export const BOOKING_MODES = ['book', 'book_or_walk_in', 'walk_in', 'waitlist'] as const;
/** How patients book a service: a time, a time or a walk-in, walk-ins only, the waitlist only. */
export type BookingMode = (typeof BOOKING_MODES)[number];

export const BOOKING_MODE_LABELS: Record<BookingMode, string> = {
  book: 'Book a time',
  book_or_walk_in: 'Book a time, or walk in',
  walk_in: 'Walk-ins only',
  waitlist: 'Waitlist only',
};

/** A branch's walk-in hours on one day (BCW-D7), wall clock in its zone. */
export type WalkInDay = {
  /** `YYYY-MM-DD`. */
  date: string;
  windows: { start: string; end: string }[];
};

// ── Conditions (BCW-D2 to D5) ────────────────────────────────────────────────

/** A condition as staff see it. */
export type BookingCondition = {
  id: string;
  /** The link name: `/<slug>/c/<code>`. */
  code: string;
  nameEn: string;
  nameFr: string | null;
  blurbEn: string | null;
  blurbFr: string | null;
  /** The OneRx starter it was copied from. */
  starterKey: string | null;
  /** The province-rules key (BCW-D4). Nothing reads it yet. */
  ruleRef: string | null;
  position: number;
  enabled: boolean;
  /** The 1–3 services it books, in order. */
  serviceIds: string[];
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateBookingCondition = {
  code?: string;
  nameEn?: string;
  nameFr?: string | null;
  blurbEn?: string | null;
  blurbFr?: string | null;
  /** Copy a starter: its words and key come with it. */
  starterKey?: string;
  ruleRef?: string | null;
  serviceIds: string[];
  enabled?: boolean;
  position?: number;
};
export type UpdateBookingCondition = Partial<CreateBookingCondition>;

/** A OneRx starter condition (BCW-D3): names and one line, no clinical rules. */
export type ConditionStarter = {
  key: string;
  name: { en: string; fr: string };
  blurb: { en: string; fr: string };
  /** The service preset it usually books (`minor-ailment`). */
  servicePreset: string;
};

/** A condition as the booking page lists it. */
export type PublicCondition = {
  id: string;
  code: string;
  name: { en: string; fr: string | null };
  blurb: { en: string | null; fr: string | null };
  /** The services it leads to that the page can book, in order. */
  serviceIds: string[];
};

// ── The waitlist on the booking page (BCW-D8 to D12) ────────────────────────

export const DAY_PARTS = ['morning', 'afternoon', 'evening'] as const;
/** Morning before noon, afternoon noon to five, evening after, on the branch's clock. */
export type DayPart = (typeof DAY_PARTS)[number];

export type PublicWaitlist = {
  /** The page may offer to join: the flag is on and an offer could reach the patient. */
  available: boolean;
  /** How long a place lasts. */
  maxDays: number;
  /** Offers can come by text here (the patient still ticks the box). */
  textOffers: boolean;
  /** Cloudflare Turnstile's site key, when joining asks for the check. */
  turnstileSiteKey: string | null;
};

export type WaitlistJoinRequest = {
  serviceId: string;
  /** Absent: any branch. */
  locationId?: string;
  conditionId?: string;
  /** ISO weekdays, 1 = Monday. Empty: any day. */
  days: number[];
  /** Empty: any time. */
  parts: DayPart[];
  /** `YYYY-MM-DD`; today when absent. */
  fromDate?: string;
  /** `YYYY-MM-DD`; at most 30 days on, 30 when absent. */
  endsOn?: string;
  patient: {
    firstName: string;
    lastName: string;
    dob: string;
    email?: string;
    phone?: string;
    province?: string;
  };
  /** "Text me when a time opens up": express consent, in the page's wording. */
  textOffers?: boolean;
  language?: 'en' | 'fr';
  /** The hidden field a person never fills in. */
  website?: string;
  turnstile?: string;
};

export type WaitlistEntryStatus =
  | 'waiting'
  | 'offered'
  | 'paused'
  | 'booked'
  | 'left'
  | 'expired'
  | 'removed';

export type WaitlistOffersBy = 'text' | 'email' | 'text_and_email';

/** The patient's own place (`/w/<token>`): nothing a stranger shouldn't read. */
export type WaitlistEntryPublic = {
  status: WaitlistEntryStatus;
  endReason: string | null;
  pharmacy: { name: string; phone: string | null };
  serviceTitle: string;
  location: { name: string } | null;
  days: number[];
  parts: DayPart[];
  fromDate: string;
  endsOn: string;
  timezone: string;
  offersBy: WaitlistOffersBy | null;
};

export type WaitlistJoinResult = WaitlistEntryPublic & {
  /** For "See or leave the waitlist": `/w/<token>`. */
  entryToken: string;
  /** Already on this waitlist: nothing changed. */
  already: boolean;
};

// ── Offers (BCW-D15 to D24) ──────────────────────────────────────────────────

export type WaitlistOfferStatus = 'open' | 'booked' | 'declined' | 'lapsed' | 'taken' | 'withdrawn';
export type WaitlistOfferMode = 'held' | 'group';

/** The offer page (`/o/<token>`). */
export type WaitlistOfferView = {
  status: WaitlistOfferStatus;
  /** `held`: kept for this patient until `expiresAt`. `group`: the first to book gets it. */
  mode: WaitlistOfferMode;
  startAt: string;
  endAt: string;
  expiresAt: string;
  timezone: string;
  pharmacy: { name: string; phone: string | null; slug: string | null };
  serviceId: string;
  serviceTitle: string;
  /** With the slug and service: what the manage link needs to offer a new time. */
  locationId: string;
  location: { name: string; address: string | null; phone: string | null };
  /** The service asks forms: the page loads them by slug and service. */
  hasForms: boolean;
  consentText: string | null;
  booking: PublicBookingResult | null;
};

export type WaitlistOfferAccept = {
  forms?: PublicBookingFormAnswers[];
  consentGiven?: boolean;
  consentTextVersion?: string;
};

// ── The staff Waitlist (BCW-D27, D28a) ───────────────────────────────────────

export type WaitlistOfferSummary = {
  id: string;
  status: WaitlistOfferStatus;
  mode: WaitlistOfferMode;
  startAt: string;
  expiresAt: string;
  createdAt: string;
  trigger: string;
};

export type WaitlistStaffEntry = {
  id: string;
  status: WaitlistEntryStatus;
  endReason: string | null;
  source: 'online' | 'staff';
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    email: string | null;
  };
  service: { id: string; title: string };
  location: { id: string; name: string } | null;
  condition: { id: string; name: string } | null;
  days: number[];
  parts: DayPart[];
  fromDate: string;
  endsAt: string;
  lapsedCount: number;
  createdAt: string;
  /** The latest few. */
  offers: WaitlistOfferSummary[];
};

export type WaitlistListQuery = {
  status?: 'waiting' | 'offered' | 'paused' | 'ended' | 'all';
  serviceId?: string;
  locationId?: string;
};

export type StaffAddWaitlist = {
  patientId: string;
  serviceId: string;
  locationId?: string;
  conditionId?: string;
  days: number[];
  parts: DayPart[];
  fromDate?: string;
  endsOn?: string;
  /** The patient said yes to texts, at the counter or on the phone. */
  textOffers?: boolean;
  language?: 'en' | 'fr';
};

export type WaitlistPolicy = { holdMinutes: 15 | 30 | 60 };

/** A free time for one entry, and whether it fits what they asked for. */
export type WaitlistSlot = {
  locationId: string;
  startAt: string;
  endAt: string;
  fits: boolean;
};

export type WaitlistHandOffer = { startAt: string; locationId: string };

/** A time held for a waitlist offer, for the calendar grid. */
export type WaitlistHeldOffer = {
  offerId: string;
  claimId: string | null;
  serviceId: string;
  locationId: string;
  providerId: string | null;
  roomId: string | null;
  startAt: string;
  endAt: string;
  expiresAt: string;
};
